import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type AgenticPanelView = 'chats' | 'dashboards' | 'processing';

interface AgenticState {
  isOpen: boolean;
  toggle: () => void;
  /** Close the panel — e.g. before the AI queue navigates to a review surface,
   *  so the panel doesn't stay open on top of it. */
  close: () => void;
  /** Which tab the agentic panel shows. Lifted here (not local panel state) so
   *  the navbar AI button can open the panel straight onto the Processing tab. */
  panelView: AgenticPanelView;
  setPanelView: (v: AgenticPanelView) => void;
  /** Open the panel directly on the Processing (AI queue) tab. */
  openProcessing: () => void;
  /** True when any agentic tab has an active run. Synced by useTabsStore.patchTab
   *  so AppHeader can read it without importing the agentic hook chain. */
  isStreaming: boolean;
  setIsStreaming: (v: boolean) => void;
  /** True when ai-processing-status has items actively processing. Synced by
   *  useProcessingStatsSync (mounted in ClientLayout) so AppHeader can read it. */
  isProcessing: boolean;
  processingPercentage: number;
  setProcessingStats: (isProcessing: boolean, percentage: number) => void;
  /** True while bulk-classify is uploading/polling. Synced by useBulkClassify. */
  isClassifying: boolean;
  setIsClassifying: (v: boolean) => void;
  appendToBuilder: ((data: unknown) => void) | null;
  setAppendToBuilder: (fn: ((data: unknown) => void) | null) => void;
  readFromBuilder: (() => unknown) | null;
  setReadFromBuilder: (fn: (() => unknown) | null) => void;
}

export const useAgenticStore = create<AgenticState>()(
  persist(
    set => ({
      isOpen: false,
      toggle: () => set(s => ({ isOpen: !s.isOpen })),
      close: () => set({ isOpen: false }),
      panelView: 'chats',
      setPanelView: v => set({ panelView: v }),
      openProcessing: () => set({ isOpen: true, panelView: 'processing' }),
      isStreaming: false,
      setIsStreaming: (v: boolean) => set({ isStreaming: v }),
      isProcessing: false,
      processingPercentage: 0,
      setProcessingStats: (isProcessing, percentage) =>
        set({ isProcessing, processingPercentage: percentage }),
      isClassifying: false,
      setIsClassifying: (v: boolean) => set({ isClassifying: v }),
      appendToBuilder: null,
      setAppendToBuilder: fn => set({ appendToBuilder: fn }),
      readFromBuilder: null,
      setReadFromBuilder: fn => set({ readFromBuilder: fn }),
    }),
    {
      name: 'agentic:panel',
      partialize: s => ({ isOpen: s.isOpen }),
    },
  ),
);
